import { MaterialCommunityIcons as Icon } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useAuth } from '../../context/AuthContext';
import { useTier } from '../../context/TierContext';
import { usePaddlePrice } from '../../hooks/usePaddlePrice';
import { useResponsive } from '../../hooks/useResponsive';
import { isDeferredTrialEligibleStatus } from '../../hooks/useTrialUnlockEligibility';
import { openDeferredTrialCheckout, PADDLE_CONFIG } from '../../lib/paddle';
import { apiService } from '../../services/apiService';
import supabase from '../../utils/supabaseClient';
import { REDESIGN_COLORS } from '../../styles/colors';
import env from '../../utils/environment';
import logger from '../../utils/logger';

/**
 * TrialUnlockModal (Monetization Step 8: two-phase trial).
 *
 * Deliberately a NEW component, NOT an overload of UpgradeModal: post-trial /
 * ineligible users still use UpgradeModal / the /pricing instant-pay path. This
 * surface is shown to any user who is currently `trial_active` (the eligibility
 * gate lives in useTrialUnlockEligibility, R8.5). The two-phase trial is the
 * default + only flow (no experiment). On unlock it calls the
 * create-trial-checkout edge function, which mints a Paddle TRANSACTION with a
 * per-user inline price (trial_period = the user's exact remaining trial days),
 * then opens the Paddle overlay against that transaction id. The resulting
 * subscription is `trialing` (GBP 0 now) and carries the `deferred_trial`
 * marker the Phase C webhook keys on.
 *
 * Visual = Claude Design "Direction A · Timeline rail": the three-beat trial
 * story (Today unlock -> reminder -> first charge) is the hero, presented as a
 * vertical rail. Desktop renders a centered card; mobile renders a bottom sheet.
 * Copy is customer-facing: no em dashes, no firstName personalisation.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   source?: string   (analytics attribution, e.g. 'trial_unlock_cta')
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const BLUE_GRAD = ['#538AF9', '#2466ED'];

// "13 Jun" style short date (no year) for the rail + headline.
const formatShort = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Trial length (whole days) for the deferred-trial inline price. This MUST match
// the server computation in create-trial-checkout (computeRemainingTrialDays) so
// the trial length / first-charge date the modal shows equals the trial_period
// the transaction is created with (Paddle then shows the same date at checkout).
//
// Monetization item #5 (clock reset on card entry): RESTART_TRIAL_ON_CARD_ADD
// mirrors the server flag. When on, adding a card grants a FRESH full cohort
// window (e.g. 7 days) from now rather than honouring only the remaining days,
// so the modal's charge date matches the restarted Paddle trial.
const DEFAULT_COHORT_DAYS = 7;
// Operator decision 2026-06-12: ship with the restart OFF (legacy remaining-days).
const RESTART_TRIAL_ON_CARD_ADD = false;
const computeRemainingTrialDays = (trialStartedAt, trialCohortDays) => {
  const cohortDays = trialCohortDays > 0 ? trialCohortDays : DEFAULT_COHORT_DAYS;
  if (RESTART_TRIAL_ON_CARD_ADD) return cohortDays;
  if (!trialStartedAt) return cohortDays;
  const startMs = new Date(trialStartedAt).getTime();
  if (Number.isNaN(startMs)) return cohortDays;
  const endMs = startMs + cohortDays * MS_PER_DAY;
  const remaining = Math.ceil((endMs - Date.now()) / MS_PER_DAY);
  if (remaining < 1) return 1;
  if (remaining > cohortDays) return cohortDays;
  return remaining;
};

// Whole-pound GBP with thousands separators (e.g. "£1,800"). Used by the
// per-listing below-market line ("This one is £1,800 under market.").
const formatPounds = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `£${Math.round(n).toLocaleString('en-GB')}`;
};

const TrialUnlockModal = ({ isOpen, onClose, source = 'trial_unlock_cta', belowMarketDelta = null }) => {
  const posthog = usePostHog();
  const router = useRouter();
  const { user } = useAuth();
  const { subscriptionStatus, trialStartedAt, trialCohortDays } = useTier();
  const { isMobile } = useResponsive();

  const [isOpening, setIsOpening] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Whole-feed below-market figures, the SAME the /pricing hero uses
  // (all_below_market_count + all_below_market_total from
  // get_my_trial_financial_metrics: every below-market car the unlock would
  // surface, not the credibility-capped top-5). Drives the "Today" beat line.
  // Fail-soft: any error or zero leaves it null and the line is omitted.
  // Mirrors the call pattern in PricingSection / UnifiedFeedScreen.
  const [belowMarket, setBelowMarket] = useState(null);
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let cancelled = false;
    supabase
      .rpc('get_my_trial_financial_metrics')
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        const row = Array.isArray(data) ? data[0] : data;
        if (row) {
          setBelowMarket({
            count: Number(row.all_below_market_count) || 0,
            total: Number(row.all_below_market_total) || 0,
          });
        }
      });
    return () => { cancelled = true; };
  }, [isOpen, user?.id]);

  // The discount is now applied server-side (create-trial-checkout attaches the
  // welcome discount to the inline price). For DISPLAY only we preview the
  // regular Monthly catalog price, optionally with the welcome discount id, so
  // the modal can show "first month" vs "then" amounts.
  const monthlyPriceId = PADDLE_CONFIG?.priceIds?.proMonthly || null;
  const welcomeCode = PADDLE_CONFIG?.discountCode || null;

  // Two live reads against the regular Monthly catalog price id:
  //  - recurringPrice (NO discount): the ongoing monthly price, e.g. GBP 24.99.
  //    This is the "then" price the user pays from the second cycle on.
  //  - firstCyclePrice (WITH the welcome discount): Paddle returns the
  //    POST-discount first-cycle total directly, e.g. GBP 12.49. We use its
  //    fullPrice as-is and must NOT use discountPrice, which would apply a
  //    second client-side cut on top of an already-discounted amount.
  const recurringPrice = usePaddlePrice({ priceId: monthlyPriceId });
  const firstCyclePrice = usePaddlePrice({
    priceId: monthlyPriceId,
    discountId: welcomeCode || undefined,
  });

  // Trial length / first-charge date. Compute N (remaining whole days, clamped
  // [1, cohort]) the SAME way the server does, and derive the charge date as
  // now + N days so the modal and the Paddle overlay agree on the exact date.
  const remainingDays = computeRemainingTrialDays(trialStartedAt, trialCohortDays);
  const trialEndDate = new Date(Date.now() + remainingDays * MS_PER_DAY);
  const endShort = formatShort(trialEndDate);
  const reminderShort = formatShort(new Date(trialEndDate.getTime() - MS_PER_DAY));

  const recurringReady = recurringPrice && !recurringPrice.isLoading && !recurringPrice.error;
  const firstReady = firstCyclePrice && !firstCyclePrice.isLoading && !firstCyclePrice.error;
  // "then" = recurring price (e.g. GBP 24.99/mo).
  const thenLabel = recurringReady && recurringPrice.fullPrice ? recurringPrice.fullPrice : null;
  // "first month" = Paddle's post-discount first-cycle total (e.g. GBP 12.49),
  // shown ONLY when a welcome discount is configured. Without a discount the two
  // reads are identical, so we suppress the first-month line.
  const firstMonthLabel =
    welcomeCode && firstReady && firstCyclePrice.fullPrice ? firstCyclePrice.fullPrice : null;

  // paywall_shown analytic on first open (StrictMode-safe single fire).
  const shownFiredRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      shownFiredRef.current = false;
      return;
    }
    if (shownFiredRef.current) return;
    shownFiredRef.current = true;
    try {
      posthog?.capture?.('trial_unlock_shown', {
        source,
        subscription_status: subscriptionStatus,
      });
    } catch (err) {
      logger.warn('[TrialUnlockModal] trial_unlock_shown emit failed:', err);
    }
  }, [isOpen, source, subscriptionStatus, posthog]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Compare plans: close the modal and route to the full /pricing comparison.
  const handleComparePlans = useCallback(() => {
    try {
      posthog?.capture?.('trial_unlock_compare_plans_clicked', { source });
    } catch (err) {
      logger.warn('[TrialUnlockModal] compare_plans emit failed:', err);
    }
    onClose?.();
    router?.push?.('/pricing');
  }, [onClose, router, posthog, source]);

  // Codex Phase E BLOCKER (R8.5): eligibility is checked when the modal OPENS,
  // but subscription_status can change while it sits open (e.g. the trial
  // expires at day-7, or another tab converts). If the user is no longer
  // trial_active, auto-dismiss so a now-invalid deferred-trial offer cannot
  // linger. The unlock click is separately re-guarded below.
  useEffect(() => {
    if (isOpen && !isDeferredTrialEligibleStatus(subscriptionStatus)) {
      logger.warn('[TrialUnlockModal] no longer trial_active while open; dismissing');
      onClose?.();
    }
  }, [isOpen, subscriptionStatus, onClose]);

  const handleUnlock = useCallback(async () => {
    if (isOpening) return;
    setCheckoutError(null);

    // Codex Phase E BLOCKER (R8.5): re-check eligibility at click time, NOT just
    // at open time. Starting a deferred trial for a user who is no longer
    // trial_active would be a forbidden second free trial. If ineligible now,
    // close and route to the instant-pay surface instead of opening checkout.
    if (!isDeferredTrialEligibleStatus(subscriptionStatus)) {
      logger.warn('[TrialUnlockModal] not trial_active at unlock time; routing to instant-pay');
      onClose?.();
      router?.push?.('/pricing');
      return;
    }

    setIsOpening(true);
    try {
      // Server mints the deferred-trial transaction (inline price with the
      // correct per-user trial_period + welcome discount + custom_data markers).
      let transactionId = null;
      try {
        const res = await apiService.invokeEdgeFunction('create-trial-checkout', {
          body: { source },
        });
        transactionId = res?.transactionId ?? null;
      } catch (err) {
        logger.warn('[TrialUnlockModal] create-trial-checkout failed:', err);
        setCheckoutError('This offer is unavailable right now. Please try again later.');
        setIsOpening(false);
        return;
      }
      if (!transactionId) {
        setCheckoutError('This offer is unavailable right now. Please try again later.');
        setIsOpening(false);
        return;
      }

      // Open the Paddle overlay against the minted transaction. The transaction
      // already carries the inline price, discount, and the deferred_trial +
      // supabase_user_id markers the Phase C webhook keys on. We re-pass the
      // COMPLETE marker set as customData ONLY when we have user.id: Paddle
      // overwrites the transaction's custom_data with whatever we pass to
      // Checkout.open, so a partial object (missing supabase_user_id) would
      // silently break the webhook's trial_carded flip. If user.id is missing,
      // pass no customData and rely on the transaction's own server-set
      // custom_data.
      const checkoutCustomData = user?.id
        ? {
            deferred_trial: 'true',
            supabase_user_id: user.id,
            upgrade_source: source,
          }
        : undefined;
      await openDeferredTrialCheckout({
        transactionId,
        email: user?.email,
        customData: checkoutCustomData,
        onSuccess: () => {
          // Webhook + Realtime are authoritative (TierContext flips to
          // trial_carded and fires the unlock toast). Dismiss locally for
          // snappy UX.
          onClose?.();
        },
        onError: (err) => {
          const isValidationError = err?.code === 'validation' || err?.type === 'front-end_error';
          if (!isValidationError) {
            setCheckoutError("That didn't go through. Please try again.");
          }
        },
        onClose: () => {
          // User dismissed the Paddle overlay without completing. Drop the
          // spinner so the CTA is interactive again. No state change (they
          // stay trial_active and can retry).
          setIsOpening(false);
        },
      });
    } catch (err) {
      logger.warn('[TrialUnlockModal] openDeferredTrialCheckout threw:', err);
      const isValidationError = err?.code === 'validation' || err?.type === 'front-end_error';
      if (!isValidationError) {
        setCheckoutError("That didn't go through. Please try again.");
      }
    } finally {
      setIsOpening(false);
    }
  }, [
    isOpening,
    user?.id,
    user?.email,
    source,
    onClose,
    subscriptionStatus,
    router,
  ]);

  // Defence-in-depth kill switch (parity with UpgradeModal).
  if (env?.isPaywallEnabled === false) return null;
  if (!isOpen) return null;

  // ---- Trial story (the three rail beats) --------------------------------
  const firstChargeBody =
    firstMonthLabel && thenLabel
      ? `${firstMonthLabel} first month, then ${thenLabel}/mo. Cancel anytime.`
      : thenLabel
        ? `${thenLabel}/mo. Cancel anytime.`
        : 'Your monthly plan price. Cancel anytime.';

  // Item #4 reassurance: the reminder beat states the genuine day-before email
  // (we send one, 1 day before). The charge beat names the exact first-charge date.
  // "Today" beat reuses the /pricing whole-feed below-market figures: how many
  // cars unlock and their combined below-market value.
  const countLabel = belowMarket?.count > 0 ? String(belowMarket.count) : null;
  const totalUnderMarketLabel = formatPounds(belowMarket?.total);
  const showValueLine = !!(countLabel && totalUnderMarketLabel);
  const steps = [
    {
      key: 'today',
      icon: 'lock-open-variant',
      when: 'Today',
      // "See all 30 cars in your feed priced £30,852 below market" with both
      // numbers green (composed in the render). Null when figures unavailable.
      showValue: showValueLine,
      countLabel,
      totalLabel: totalUnderMarketLabel,
      // Fallback body only when the figures are unavailable, so the beat is
      // never empty.
      body: showValueLine ? null : 'Full Pro access unlocks now. £0.00 due.',
      active: true,
    },
    {
      key: 'reminder',
      icon: 'bell-outline',
      when: reminderShort || 'Before billing',
      body: "We'll email you 1 day before.",
      active: false,
    },
    {
      key: 'charge',
      icon: 'credit-card-outline',
      when: endShort ? `${endShort} · first charge` : 'Trial ends',
      body: firstChargeBody,
      active: false,
    },
  ];

  // Item #3 (per-listing delta) framing. Restart promise removed: the flag is
  // off, so the copy promises only the remaining free days, which is what the
  // trial_period actually grants.
  const headlineSecond = 'Free for the rest of your trial';
  const deltaLabel = formatPounds(belowMarketDelta);
  const subline = deltaLabel
    ? `This one is ${deltaLabel} under market. Add a card to see it, plus every other match, free for the rest of the week. You are not charged today.`
    : endShort
      ? `Add a card and everything unlocks for the rest of your free week, through ${endShort}. You are not charged today.`
      : 'Add a card and everything unlocks for the rest of your free week. You are not charged today.';

  // ---- Shared modal body (rail + CTA + footer) ---------------------------
  const Body = (
    <>
      <View style={styles.pillRow}>
        <Text style={styles.pill}>Pro trial</Text>
      </View>

      <Text style={styles.headline}>
        Unlock Full Pro{'\n'}
        {headlineSecond}
      </Text>
      <Text style={styles.subline}>{subline}</Text>

      <View style={styles.rail}>
        {steps.map((s, i) => {
          const last = i === steps.length - 1;
          return (
            <View key={s.key} style={styles.railRow}>
              <View style={styles.nodeCol}>
                {!last ? <View style={styles.connector} /> : null}
                {s.active ? (
                  <LinearGradient
                    colors={BLUE_GRAD}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[styles.node, styles.nodeActive]}
                  >
                    <Icon name={s.icon} size={20} color="#FFFFFF" />
                  </LinearGradient>
                ) : (
                  <View style={[styles.node, styles.nodeIdle]}>
                    <Icon name={s.icon} size={20} color="#9EBBFF" />
                  </View>
                )}
              </View>
              <View style={styles.railText}>
                <Text style={styles.railWhen}>{s.when}</Text>
                {s.showValue ? (
                  <Text style={styles.railSubtitle}>
                    {'See all '}
                    <Text style={styles.railSubtitleAmount}>{s.countLabel}</Text>
                    {' cars in your feed priced '}
                    <Text style={styles.railSubtitleAmount}>{s.totalLabel}</Text>
                    {' below market'}
                  </Text>
                ) : null}
                {s.body ? <Text style={styles.railBody}>{s.body}</Text> : null}
              </View>
            </View>
          );
        })}
      </View>

      {checkoutError ? (
        <View style={styles.errorBlock} accessibilityRole="alert">
          <Text style={styles.errorText}>{checkoutError}</Text>
        </View>
      ) : null}

      <Pressable
        onPress={handleUnlock}
        disabled={isOpening}
        accessibilityRole="button"
        accessibilityLabel="Add card to unlock the trial"
        accessibilityState={{ disabled: isOpening }}
        style={isOpening ? styles.ctaDisabled : null}
      >
        <LinearGradient
          colors={BLUE_GRAD}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.cta}
        >
          {isOpening ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <>
              <Text style={styles.ctaText}>Add card</Text>
              <Icon name="arrow-right" size={16} color="#FFFFFF" />
            </>
          )}
        </LinearGradient>
      </Pressable>
      <Text style={styles.ctaSub}>
        {endShort
          ? `£0.00 due now. First charge ${endShort}. Cancel in one tap.`
          : '£0.00 due now. Cancel in one tap.'}
      </Text>

      <Pressable
        onPress={handleComparePlans}
        disabled={isOpening}
        accessibilityRole="link"
        accessibilityLabel="Compare plans"
        style={styles.switchNote}
      >
        <Icon name="swap-horizontal" size={14} color="#9EBBFF" />
        <Text style={styles.compareLink}>Compare plans</Text>
      </Pressable>

      <View style={styles.divider} />

      <View style={styles.footerRow}>
        <View style={styles.secureWrap}>
          <Icon name="lock-outline" size={12} color="rgba(255,255,255,0.4)" />
          <Text style={styles.secureText}>Secure checkout by Paddle</Text>
        </View>
        <Pressable
          onPress={handleClose}
          disabled={isOpening}
          accessibilityRole="button"
          accessibilityLabel="Maybe later"
        >
          <Text style={styles.maybeLater}>Maybe later</Text>
        </Pressable>
      </View>
    </>
  );

  return (
    <Modal
      animationType={isMobile ? 'slide' : 'fade'}
      transparent
      visible={isOpen}
      onRequestClose={handleClose}
    >
      <Pressable
        style={[styles.backdrop, isMobile && styles.backdropMobile]}
        onPress={Platform.OS === 'web' ? handleClose : undefined}
        accessibilityRole="button"
        accessibilityLabel="Dismiss"
      >
        {isMobile ? (
          <Pressable style={styles.sheet} onPress={() => {}}>
            <View style={styles.grabber} />
            {Body}
          </Pressable>
        ) : (
          <Pressable style={styles.card} onPress={() => {}}>
            {Body}
          </Pressable>
        )}
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  // ---- Backdrop / shells ----
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(5, 8, 14, 0.62)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  backdropMobile: {
    justifyContent: 'flex-end',
    padding: 0,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: '#101622',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    paddingHorizontal: 28,
    paddingTop: 26,
    paddingBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 30 },
    shadowOpacity: 0.55,
    shadowRadius: 80,
  },
  sheet: {
    width: '100%',
    backgroundColor: '#101622',
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    borderTopWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -20 },
    shadowOpacity: 0.6,
    shadowRadius: 60,
  },
  grabber: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignSelf: 'center',
    marginBottom: 14,
  },

  // ---- Header ----
  pillRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  pill: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 11.5,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: '#9EBBFF',
    backgroundColor: 'rgba(20,76,184,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(59,123,240,0.3)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 99,
    overflow: 'hidden',
  },
  headline: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 30,
    lineHeight: 35,
    letterSpacing: -0.6,
    color: '#FFFFFF',
    marginBottom: 10,
  },
  subline: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14.5,
    lineHeight: 22,
    color: 'rgba(255,255,255,0.62)',
    marginBottom: 22,
  },

  // ---- Timeline rail ----
  rail: {
    marginBottom: 22,
  },
  railRow: {
    flexDirection: 'row',
    gap: 16,
    paddingBottom: 22,
  },
  nodeCol: {
    width: 44,
    flexShrink: 0,
    alignItems: 'center',
  },
  connector: {
    position: 'absolute',
    top: 44,
    height: 22,
    width: 2.5,
    borderRadius: 2,
    backgroundColor: 'rgba(83,138,249,0.45)',
    zIndex: 0,
  },
  node: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  nodeActive: {
    shadowColor: 'rgba(36,102,237,0.4)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 16,
  },
  nodeIdle: {
    backgroundColor: 'rgba(20,76,184,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  railText: {
    flex: 1,
    paddingTop: 2,
  },
  railWhen: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    letterSpacing: -0.15,
    color: '#FFFFFF',
    marginBottom: 3,
  },
  railSubtitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13.5,
    lineHeight: 19,
    letterSpacing: -0.1,
    color: 'rgba(255,255,255,0.6)',
    marginBottom: 3,
  },
  railSubtitleAmount: {
    color: '#22C55E',
  },
  railBody: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13.5,
    lineHeight: 20,
    color: 'rgba(255,255,255,0.6)',
  },

  // ---- Error ----
  errorBlock: {
    marginBottom: 14,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,107,107,0.35)',
    backgroundColor: 'rgba(255,107,107,0.08)',
  },
  errorText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: REDESIGN_COLORS?.scorePoor || '#FF6B6B',
    lineHeight: 18,
  },

  // ---- CTA ----
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 99,
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  ctaDisabled: {
    opacity: 0.6,
  },
  ctaText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    letterSpacing: -0.16,
    color: '#FFFFFF',
  },
  ctaSub: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12.5,
    lineHeight: 19,
    color: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
    marginTop: 10,
  },

  // ---- Switch note + footer ----
  switchNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    marginTop: 16,
  },
  compareLink: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 13,
    color: '#9EBBFF',
    textDecorationLine: 'underline',
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.07)',
    marginVertical: 16,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  secureWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  secureText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
  },
  maybeLater: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: 'rgba(255,255,255,0.45)',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
});

export default TrialUnlockModal;
